import { Appointment, AppointmentID } from "./Appointment";
import { CustomerID } from "./Customer";
import { DealerLocationID } from "./DealerLocation";
import { KeyLockerCommunicationID } from "./KeylockerCommunication";
import { LANGUAGE_CODES } from "./Language";
import { User, UserID } from "./User";

export type KeyLockerID = number;

export type KeyLockerBoxID = number;

export type KeyLockerBoxHistoryID = number;

export type KeyLockerPIN = number;

export type KeyLockerRemarkID = number;

export type KeyLockerCallForHelpID = number;

export enum KEYLOCKER_BOX_EVENTS {
  BoxAssigned = 1,
  CustomerOpenBox = 2,
  CustomerCloseBox = 3,
  UserOpenBox = 4,
  DropKeyAborted = 5,
  UserRequestedPickupScan = 6, // legacy, users used to request scan from the tablet, now it's done from the keylocker and we don't know yet for which box at that point
  UserPickupScanned = 7,
  RelayError = 8,
  Deleted = 9,
  Enabled = 10,
  Disabled = 11,
  Updated = 12,
  DoorOpened = 13,
  DoorOpeningFailed = 14,
  DoorClosed = 15,
  DoorClosingFailed = 16,
  UserRequestedDropBackScan = 17, // legacy, users used to request scan from the tablet, now it's done from the keylocker and we don't know yet for which box at that point
  UserDropBackScan = 18,
  UserRequestedAssistScan = 19, // legacy, users used to request scan from the tablet, now it's done from the keylocker and we don't know yet for which box at that point
  UserAssistScan = 20,
  CustomerPickUpKeyBack = 21,
  AdminRequestPin = 22,
  AdminOpenedDoor = 23,
  UserVerifyPickupScan = 24,
  UserVerifyDropBackScan = 25,
  UserVerifyAssistScan = 26
}

export interface KeyLocker {
  created_on: string;
  updated_on: string;
  id: KeyLockerID;
  name: string; // Name given to this KeyLocker
  frontend_version: number | null; // Version of the keylocker Frontend
  active: boolean; // Allows users and customer to use this KeyLocker
  box_count: number; // Amount of boxes for this KeyLocker, by default 16
  active_box_count: number; // Amount of boxes that can be used
  key_count: number; // Amount of keys stored in the KeyLocker
  dealer_location_id: DealerLocationID; // DealerLocation Identifier to which this KeyLocker belongs to
  is_2fa_to_drop_key_enabled: boolean; // Enables Two Factor Authentication
  keylocker_box_id?: KeyLockerBoxID;
  box_number?: number;
  keylocker_communication_id?: KeyLockerCommunicationID;
  vat?: number; // VAT of the DealerLocation
  include_vat?: boolean; // IncludeVAT of the DealerLocation
  language_code?: LANGUAGE_CODES; // LanguageCode of the DealerLocation
}

export interface KeyLockerBox {
  created_on: string;
  updated_on: string;
  id: KeyLockerBoxID;
  dealer_location_id: DealerLocationID; // DealerLocation Identifier
  box_number: number; // Sequence number this box is given in relation to the other boxes of the KeyLocker
  active: boolean; // This box can be used to drop keys
  is_empty: boolean; // There are no keys currently stored in this box
  appointment_id: AppointmentID | null; // If a key is stored, this shows what Appointment Identifier that the key belongs to
  keylocker_communication_id: KeyLockerCommunicationID | null; // If a key is stored, this shows what KeyLockerCommunication Identifier that the key belongs to
  keylocker_id: KeyLockerID; // KeyLocker Identifier to which this box belongs to
  appointment?: Appointment;
  private_key?: string;
  name?: string;
}

export interface KeyLockerBoxEvent {
  created_on: string;
  updated_on: string;
  keylocker_box_history_id: KeyLockerBoxHistoryID;
  keylocker_box_id: KeyLockerBoxID;
  keylocker_box_event_id: KEYLOCKER_BOX_EVENTS;
  message: string;
  user: UserID | null;
  customer_id: CustomerID | null;
  appointment_id: AppointmentID | null;
  keylocker_communication_id: KeyLockerCommunicationID | null;
  scan_request_key: string | null;
}

export interface KeyLockerRemark {
  created_on: string;
  updated_on: string;
  id: KeyLockerRemarkID;
  description: string; // Reason for the remark
  customer_name: string; // Customer name
  receptionist_handled: boolean; // Receptionist has handled the remark
  visible_to_mechanic: boolean; // The mechanic can see this remark and can work on it
  keylocker_communication_id: KeyLockerCommunicationID; // The KeyLockerCommunication that the remark originates rom
  appointment_id?: AppointmentID; // Appointment Identifier
}

export interface KeyLockerCallForHelp {
  created_on: string;
  updated_on: string;
  keylocker_call_for_help_id: KeyLockerCallForHelpID;
  keylocker_id: KeyLockerID; // KeyLocker Identifier
  dealer_location_id: DealerLocationID; // DealerLocation Identifier
  appointment_id: AppointmentID | null; // Appointment Identifier
  handled_by_id: UserID | null; // Receptionist who handled this call for help
  handled_by?: User; // Optional embedded Receptionist
}
